import axiosInstance from "../../utils/AxiosInstance";
import { toast } from "react-toastify";
import { useAuthHeader, useAuthUser } from "react-auth-kit";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useGetPatients } from "../PatientComponents";
import { useGetOrganizations } from "../HandlingComponents";
import { useGetKits } from "../KitComponents";
import { deviceCategories, devices } from ".";
import { prettify, toastError, toastSuccess } from "../../utils/utils";
import { buildDevicePayload } from "./deviceFormUtils";

// GET /devices
export const useGetDevices = (filter = "all") => {
  const authHeader = useAuthHeader();
  const patientsQuery = useGetPatients();
  const organizationsQuery = useGetOrganizations();
  const kitsQuery = useGetKits();

  return useQuery({
    queryKey: ["devices", filter],
    queryFn: async () => {
      const res = await axiosInstance.get(`/devices`, {
        headers: { Authorization: authHeader() },
      });
      let data = res.data;
      const organizations = organizationsQuery.data?.organizations ?? [];
      const patients = patientsQuery.data?.patients ?? [];
      const kits = kitsQuery.data?.kits ?? [];

      let _devices = [];

      // get all device_types beloging to the "filter" category
      let filteredDeviceTypes = [];
      devices.forEach((el) => {
        if (el.category === filter) filteredDeviceTypes.push(el.device_type);
      });

      // now filter by device_type
      const filteredDevices =
        filter === "all"
          ? [...data.devices]
          : data.devices.filter((el) =>
              filteredDeviceTypes.includes(el.device_type)
            );

      // iterate and set patient and doctor informations for rendering
      for (let d of filteredDevices) {
        let devicePatient = patients.find((el) => el.id === d.patient_id);

        let patientInfo =
          devicePatient === undefined
            ? "Non associato"
            : `${devicePatient.first_name} ${devicePatient.last_name}`;

        const orgId = d.org_id ?? devicePatient?.org_id ?? null;
        const org = organizations.find((o) => o.id === orgId);
        const kit = kits.find((k) => k.id === d.kit_id);

        _devices.push({
          ...d,
          patient_info: patientInfo,
          org_name: org?.name ?? d.org_name ?? "",
          kit_name: kit?.name ?? "",
          // device_type_label: `${
          //   devices.find((el) => el.id === d.device_type).category
          // } / ${prettify(devices.find((el) => el.id === d.device_type).label)}`,
        });
      }
      data["devices"] = _devices;
      return data;
    },
    enabled:
      patientsQuery.isSuccess &&
      organizationsQuery.isSuccess &&
      kitsQuery.isSuccess,
  });
};

// GET /devices/:device_id
export const useGetDeviceById = (device_id) => {
  const authHeader = useAuthHeader();

  return useQuery({
    queryKey: ["devices", device_id],
    queryFn: async () => {
      const res = await axiosInstance.get(`/devices/${device_id}`, {
        headers: { Authorization: authHeader() },
      });
      return res.data;
    },
    enabled: device_id !== undefined && device_id !== null,
  });
};

// GET /devices/patient/:patient_id
export const useGetPatientDevices = (patient_id) => {
  const authHeader = useAuthHeader();

  return useQuery({
    queryKey: ["devices", "patient", patient_id],
    queryFn: async () => {
      const res = await axiosInstance.get(`/devices/patient/${patient_id}`, {
        headers: { Authorization: authHeader() },
      });
      return res.data;
    },
    enabled: patient_id !== undefined && patient_id !== null,
  });
};

// === PUT /devices/:device_id ===
export const usePutDeviceById = (device_id) => {
  const authHeader = useAuthHeader();
  const authUser = useAuthUser();
  const queryClient = useQueryClient();
  const getOriginalData = useGetDeviceById(device_id);

  return useMutation({
    mutationFn: async (editedDevice) => {
      if (editedDevice === undefined) return;
      const deviceId = editedDevice.id ?? device_id;
      const { id: _id, ...payload } = buildDevicePayload(
        editedDevice,
        authUser
      );

      const res = await axiosInstance.put(`/devices/${deviceId}`, payload, {
        headers: { Authorization: authHeader() },
      });
      return res.data;
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries(["devices"]);
      queryClient.invalidateQueries(["patients"]);
      queryClient.invalidateQueries(["devices", variables.id]);
      if (variables.kit_id != null) {
        queryClient.invalidateQueries(["kits", variables.kit_id]);
      }
      if (variables.patient_id != null) {
        queryClient.invalidateQueries([
          "devices",
          "patient",
          variables.patient_id,
        ]);
      }
      const isAssociation =
        variables.patient_id != null && variables.patient_id !== undefined;
      toast.success(
        isAssociation
          ? "Dispositivo associato al paziente"
          : "Dettagli dispositivo aggiornati!",
        toastSuccess
      );
    },
    onError: (data, variables, context) => {
      let newData =
        device_id === null
          ? queryClient.getQueryData(["devices", variables.id])
          : getOriginalData.data;
      queryClient.setQueryData(["devices", device_id], (oldData) => {
        return {
          ...oldData,
          ...newData,
        };
      });
      toast.error("Dettagli dispositivo non aggiornati", toastError);
    },
  });
};

// === PUT /devices/:device_id ===
export const useDissociateFromPatient = () => {
  const authHeader = useAuthHeader();
  const authUser = useAuthUser();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (editedDevice) => {
      const deviceId = editedDevice.id;
      const role = authUser()?.role;
      const { id: _id, ...body } = editedDevice;
      const payload =
        role === 2 || role === 3
          ? { patient_id: null }
          : { ...body, patient_id: null };

      const res = await axiosInstance.put(`/devices/${deviceId}`, payload, {
        headers: {
          Authorization: authHeader(),
        },
      });
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries(["patients"]);
      queryClient.invalidateQueries(["devices"]);
      if (variables.patient_id != null) {
        queryClient.invalidateQueries(["patients", variables.patient_id]);
        queryClient.invalidateQueries([
          "devices",
          "patient",
          variables.patient_id,
        ]);
      }
      queryClient.invalidateQueries(["devices", variables.id]);
      toast.success("Dispositivo dissociato dal paziente", toastSuccess);
    },
    onError: () => {
      toast.error("Dettagli dispositivo non aggiornati", toastError);
    },
  });
};

// === POST /devices ===
export const useNewDevice = (closeModal) => {
  const authHeader = useAuthHeader();
  const authUser = useAuthUser();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (newDevice) => {
      if (authUser()?.role !== 1) {
        throw new Error("Non autorizzato a creare dispositivi");
      }
      const res = await axiosInstance.post(`/devices`, newDevice, {
        headers: {
          Authorization: authHeader(),
        },
      });
      return res.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(["patients"]); // update views inside Doctors page
      queryClient.invalidateQueries(["devices"]); // update views inside Doctors page
      toast.success("Dispositivo aggiunto con successo!", toastSuccess);
      closeModal();
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.error || "Errore nell'aggiunta del dispositivo";
      toast.error(errorMessage, toastError);
    },
  });
};
